import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Loader2, ChevronDown, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { insertLenderProductSchema, type LenderProduct, type Lender } from "@shared/schema";
import { z } from "zod";

// Extended schema with eligibility validation (AC006)
// Wraps the base schema and adds eligibility range validation
const productFormSchema = insertLenderProductSchema.superRefine((data, ctx) => {
  const parseNum = (v: unknown) => (v == null || v === "" ? null : parseFloat(String(v)));

  const minDscr = parseNum(data.minDscr);
  if (minDscr !== null && (minDscr < 0.1 || minDscr > 10.0)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Min DSCR must be between 0.1 and 10.0", path: ["minDscr"] });
  }

  const minIcr = parseNum(data.minIcr);
  if (minIcr !== null && (minIcr < 0.1 || minIcr > 20.0)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Min ICR must be between 0.1 and 20.0", path: ["minIcr"] });
  }

  const maxDebtToEbitda = parseNum(data.maxDebtToEbitda);
  if (maxDebtToEbitda !== null && (maxDebtToEbitda < 0.1 || maxDebtToEbitda > 20.0)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Max Debt-to-EBITDA must be between 0.1 and 20.0", path: ["maxDebtToEbitda"] });
  }
});

type ProductFormData = z.infer<typeof productFormSchema>;

interface ProductFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  product?: LenderProduct;
  defaultLenderId?: string;
  onProductCreated?: (product: LenderProduct) => void;
}

export function ProductFormModal({
  open,
  onOpenChange,
  product,
  defaultLenderId,
  onProductCreated,
}: ProductFormModalProps) {
  const { toast } = useToast();
  const isEdit = !!product;
  const [eligibilityOpen, setEligibilityOpen] = useState(false);

  // Fetch lenders for dropdown
  const { data: lenders } = useQuery<Lender[]>({
    queryKey: ["/api/crm/lenders"],
    enabled: open,
  });

  const defaultValues: Partial<ProductFormData> = {
    lenderId: defaultLenderId || "",
    productName: "",
    productCode: "",
    productCategory: "term_loan",
    loanType: "residential",
    minTermMonths: undefined,
    maxTermMonths: undefined,
    interestRateType: "variable",
    baseRateSource: "RBA_cash",
    currentBaseRate: "",
    upfrontRate: "",
    trailRate: "",
    status: "active",
    // Eligibility fields
    minDscr: null,
    minIcr: null,
    maxDebtToEbitda: null,
    requiresFullDoc: true,
    requiresExistingCustomer: false,
    acceptableSecurityTypes: null,
    acceptableEntityTypes: null,
    geographicRestrictions: null,
  };

  // Helper: check if any eligibility field has a value (for auto-expand)
  const hasEligibilityCriteria = (p: LenderProduct | undefined): boolean => {
    if (!p) return false;
    return (
      p.minDscr != null ||
      p.minIcr != null ||
      p.maxDebtToEbitda != null ||
      (Array.isArray(p.acceptableSecurityTypes) && p.acceptableSecurityTypes.length > 0) ||
      (Array.isArray(p.acceptableEntityTypes) && p.acceptableEntityTypes.length > 0) ||
      (Array.isArray(p.geographicRestrictions) && p.geographicRestrictions.length > 0) ||
      p.requiresExistingCustomer === true
    );
  };

  // Helper: convert array to comma-separated string for the text input
  const arrayToString = (arr: string[] | null | undefined): string => {
    if (!arr || arr.length === 0) return "";
    return arr.join(", ");
  };

  // Helper: convert comma-separated string to array (null if empty)
  const stringToArray = (str: string | null | undefined): string[] | null => {
    if (!str || str.trim() === "") return null;
    return str.split(",").map((s) => s.trim()).filter((s) => s.length > 0);
  };

  // Helper: Convert decimal to percentage for display (0.05 -> 5)
  const decimalToPercent = (value: string | null | undefined): string => {
    if (!value || value === "") return "";
    const decimal = parseFloat(value);
    if (isNaN(decimal)) return "";
    return (decimal * 100).toString();
  };

  // Helper: Convert percentage to decimal for storage (5 -> 0.05)
  const percentToDecimal = (value: string | null | undefined): string => {
    if (!value || value === "") return "0.0000";
    const percent = parseFloat(value);
    if (isNaN(percent)) return "0.0000";
    return (percent / 100).toFixed(4);
  };

  const form = useForm<ProductFormData>({
    resolver: zodResolver(productFormSchema),
    defaultValues,
    mode: "onChange",
    criteriaMode: "all",
  });

  useEffect(() => {
    if (open) {
      if (product) {
        // Only pass form-relevant fields to reset, excluding database-only fields
        // that would cause Zod validation to fail (id, createdAt, updatedAt, etc.)
        // Convert percentage fields from decimal (0.05) to percentage (5) for display
        form.reset({
          lenderId: product.lenderId,
          productName: product.productName,
          productCode: product.productCode,
          productCategory: product.productCategory,
          loanType: product.loanType,
          minTermMonths: product.minTermMonths ?? undefined,
          maxTermMonths: product.maxTermMonths ?? undefined,
          interestRateType: product.interestRateType ?? undefined,
          baseRateSource: product.baseRateSource ?? undefined,
          // Convert decimal to percentage for display (ISSUE-054)
          currentBaseRate: decimalToPercent(product.currentBaseRate),
          upfrontRate: decimalToPercent(product.upfrontRate),
          trailRate: decimalToPercent(product.trailRate),
          status: product.status ?? undefined,
          // Eligibility fields
          minDscr: product.minDscr ?? null,
          minIcr: product.minIcr ?? null,
          maxDebtToEbitda: product.maxDebtToEbitda ?? null,
          requiresFullDoc: product.requiresFullDoc ?? true,
          requiresExistingCustomer: product.requiresExistingCustomer ?? false,
          acceptableSecurityTypes: product.acceptableSecurityTypes ?? null,
          acceptableEntityTypes: product.acceptableEntityTypes ?? null,
          geographicRestrictions: product.geographicRestrictions ?? null,
        });
        // Auto-expand eligibility section if criteria are set (AC007)
        setEligibilityOpen(hasEligibilityCriteria(product));
      } else {
        form.reset(defaultValues);
        setEligibilityOpen(false);
        if (defaultLenderId) {
          form.setValue("lenderId", defaultLenderId);
        }
      }
    }
  }, [open, product, defaultLenderId, form]);

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      form.reset(defaultValues);
      setEligibilityOpen(false);
    }
    onOpenChange(newOpen);
  };

  const createMutation = useMutation({
    mutationFn: async (data: ProductFormData) => {
      // Ensure lenderId is set (required for the nested route)
      const lenderId = data.lenderId || defaultLenderId;
      if (!lenderId) {
        throw new Error("Lender ID is required");
      }

      // Convert percentage inputs to decimals for storage (ISSUE-054)
      const dataWithDecimals = {
        ...data,
        currentBaseRate: percentToDecimal(data.currentBaseRate),
        upfrontRate: percentToDecimal(data.upfrontRate),
        trailRate: percentToDecimal(data.trailRate),
        // Ensure empty eligibility inputs coerce to null (mirrors updateMutation)
        minDscr: data.minDscr || null,
        minIcr: data.minIcr || null,
        maxDebtToEbitda: data.maxDebtToEbitda || null,
      };

      const response = await apiRequest("POST", `/api/crm/lenders/${lenderId}/products`, dataWithDecimals);
      return response.json();
    },
    onSuccess: async (newProduct: LenderProduct) => {
      // Use predicate to match ALL lender-related queries (fixes ISSUE-072)
      queryClient.invalidateQueries({
        predicate: (query) =>
          Array.isArray(query.queryKey) &&
          query.queryKey.some((key) =>
            typeof key === "string" && key.includes("/api/crm/lenders")
          ),
      });
      toast({
        title: "Product created",
        description: "The lender product has been created successfully.",
      });
      onOpenChange(false);
      form.reset(defaultValues);

      if (onProductCreated) {
        onProductCreated(newProduct);
      }
    },
    onError: (error: Error) => {
      const isCodeDuplicate = error.message.includes("product_code") ||
                              error.message.includes("duplicate key") ||
                              error.message.includes("unique constraint");

      toast({
        title: "Error",
        description: isCodeDuplicate
          ? "A product with this code already exists. Please use a different code."
          : error.message,
        variant: "destructive",
      });

      if (isCodeDuplicate) {
        form.setError("productCode", {
          type: "manual",
          message: "This product code is already in use",
        });
      }
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: ProductFormData) => {
      // Convert percentage inputs to decimals for storage (ISSUE-054)
      // Eligibility numeric fields are passed as-is (null or string)
      const dataWithDecimals = {
        ...data,
        currentBaseRate: percentToDecimal(data.currentBaseRate),
        upfrontRate: percentToDecimal(data.upfrontRate),
        trailRate: percentToDecimal(data.trailRate),
        // Ensure null eligibility numerics are null (not empty string)
        minDscr: data.minDscr || null,
        minIcr: data.minIcr || null,
        maxDebtToEbitda: data.maxDebtToEbitda || null,
      };
      return apiRequest("PATCH", `/api/crm/lender-products/${product?.id}`, dataWithDecimals);
    },
    onSuccess: () => {
      // Use predicate to match ALL lender-related queries (fixes ISSUE-072)
      queryClient.invalidateQueries({
        predicate: (query) =>
          Array.isArray(query.queryKey) &&
          query.queryKey.some((key) =>
            typeof key === "string" && key.includes("/api/crm/lenders")
          ),
      });
      toast({
        title: "Product updated",
        description: "The lender product has been updated successfully.",
      });
      onOpenChange(false);
    },
    onError: (error: Error) => {
      const isCodeDuplicate = error.message.includes("product_code") ||
                              error.message.includes("duplicate key") ||
                              error.message.includes("unique constraint");

      toast({
        title: "Error",
        description: isCodeDuplicate
          ? "A product with this code already exists. Please use a different code."
          : error.message,
        variant: "destructive",
      });

      if (isCodeDuplicate) {
        form.setError("productCode", {
          type: "manual",
          message: "This product code is already in use",
        });
      }
    },
  });

  const onSubmit = (data: ProductFormData) => {
    if (isEdit) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="product-form-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Product" : "Add Product"}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Update lender product information"
              : "Create a new lender product"}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold">Basic Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="lenderId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Lender *</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value || undefined}
                        disabled={!!defaultLenderId || isEdit}
                      >
                        <FormControl>
                          <SelectTrigger data-testid="select-lender">
                            <SelectValue placeholder="Select lender" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {lenders && lenders.length > 0 ? (
                            lenders.map((lender) => (
                              <SelectItem key={lender.id} value={lender.id}>
                                {lender.lenderName}
                              </SelectItem>
                            ))
                          ) : (
                            <SelectItem value="no-lenders" disabled>
                              No lenders available
                            </SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                      <FormDescription className="text-xs">
                        {defaultLenderId || isEdit
                          ? "Lender cannot be changed after creation"
                          : "Select the lender for this product"}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="productName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Product Name *</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder="Business Term Loan"
                          data-testid="input-product-name"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="productCode"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Product Code *</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder="BTL-001"
                          data-testid="input-product-code"
                        />
                      </FormControl>
                      <FormDescription className="text-xs">
                        Unique identifier for this product
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="productCategory"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Product Category *</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value || undefined}
                      >
                        <FormControl>
                          <SelectTrigger data-testid="select-product-category">
                            <SelectValue placeholder="Select category" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="term_loan">Term Loan</SelectItem>
                          <SelectItem value="line_of_credit">Line of Credit</SelectItem>
                          <SelectItem value="overdraft">Overdraft</SelectItem>
                          <SelectItem value="equipment_finance">Equipment Finance</SelectItem>
                          <SelectItem value="invoice_finance">Invoice Finance</SelectItem>
                          <SelectItem value="trade_finance">Trade Finance</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="loanType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Loan Type *</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value || undefined}
                      >
                        <FormControl>
                          <SelectTrigger data-testid="select-loan-type">
                            <SelectValue placeholder="Select loan type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="residential">Residential</SelectItem>
                          <SelectItem value="commercial">Commercial</SelectItem>
                          <SelectItem value="smsf">SMSF</SelectItem>
                          <SelectItem value="construction">Construction</SelectItem>
                          <SelectItem value="development">Development</SelectItem>
                          <SelectItem value="industrial">Industrial</SelectItem>
                          <SelectItem value="rural">Rural</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="status"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Status</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value || undefined}
                      >
                        <FormControl>
                          <SelectTrigger data-testid="select-status">
                            <SelectValue placeholder="Select status" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="inactive">Inactive</SelectItem>
                          <SelectItem value="suspended">Suspended</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            {/* Term Limits */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold">Term Limits</h3>
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="minTermMonths"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Min Term (months)</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          value={field.value ?? ""}
                          type="number"
                          placeholder="12"
                          data-testid="input-min-term"
                          onChange={(e) => {
                            const value = e.target.value;
                            field.onChange(value === "" ? undefined : Number(value));
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="maxTermMonths"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Max Term (months)</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          value={field.value ?? ""}
                          type="number"
                          placeholder="120"
                          data-testid="input-max-term"
                          onChange={(e) => {
                            const value = e.target.value;
                            field.onChange(value === "" ? undefined : Number(value));
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            {/* Interest Rate Configuration */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold">Interest Rate Configuration</h3>
              <div className="grid grid-cols-3 gap-4">
                <FormField
                  control={form.control}
                  name="interestRateType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Rate Type</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value || undefined}
                      >
                        <FormControl>
                          <SelectTrigger data-testid="select-rate-type">
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="variable">Variable</SelectItem>
                          <SelectItem value="fixed">Fixed</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="baseRateSource"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Base Rate Source</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          value={field.value || ""}
                          placeholder="RBA_cash"
                          data-testid="input-base-rate-source"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="currentBaseRate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Interest Rate (%)</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          value={field.value || ""}
                          type="number"
                          step="0.01"
                          placeholder="4.5"
                          data-testid="input-current-base-rate"
                        />
                      </FormControl>
                      <FormDescription className="text-xs">
                        Enter as percentage (e.g., 4.5 for 4.5%)
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            {/* Commission Rates */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold">Commission Rates</h3>
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="upfrontRate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Upfront Commission (%)</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          value={field.value || ""}
                          type="number"
                          step="0.01"
                          placeholder="2.5"
                          data-testid="input-upfront-rate"
                        />
                      </FormControl>
                      <FormDescription className="text-xs">
                        Enter as percentage (e.g., 2.5 for 2.5%)
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="trailRate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Trail Commission (%)</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          value={field.value || ""}
                          type="number"
                          step="0.01"
                          placeholder="0.1"
                          data-testid="input-trail-rate"
                        />
                      </FormControl>
                      <FormDescription className="text-xs">
                        Enter as percentage (e.g., 0.1 for 0.1%)
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            {/* Eligibility Criteria — collapsible section (AC001, AC007) */}
            <Collapsible open={eligibilityOpen} onOpenChange={setEligibilityOpen}>
              <div className="border rounded-md">
                <CollapsibleTrigger asChild>
                  <button
                    type="button"
                    className="flex w-full items-center justify-between px-4 py-3 text-sm font-semibold hover:bg-muted/50 transition-colors"
                    data-testid="eligibility-section-trigger"
                  >
                    <span>Eligibility Criteria</span>
                    {eligibilityOpen ? (
                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    )}
                  </button>
                </CollapsibleTrigger>
                <CollapsibleContent data-testid="eligibility-section-content">
                  <div className="px-4 pb-4 pt-2 space-y-4 border-t">
                    <p className="text-xs text-muted-foreground">
                      All eligibility fields are optional. Leave blank for no restriction.
                    </p>

                    {/* Numeric thresholds row 1 */}
                    <div className="grid grid-cols-3 gap-4">
                      <FormField
                        control={form.control}
                        name="minDscr"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Min DSCR</FormLabel>
                            <FormControl>
                              <Input
                                type="number"
                                step="0.01"
                                placeholder="e.g. 1.25"
                                data-testid="input-min-dscr"
                                value={field.value ?? ""}
                                onChange={(e) => field.onChange(e.target.value === "" ? null : e.target.value)}
                              />
                            </FormControl>
                            <FormDescription className="text-xs">0.1 – 10.0</FormDescription>
                            <FormMessage data-testid="error-min-dscr" />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="minIcr"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Min ICR</FormLabel>
                            <FormControl>
                              <Input
                                type="number"
                                step="0.01"
                                placeholder="e.g. 1.5"
                                data-testid="input-min-icr"
                                value={field.value ?? ""}
                                onChange={(e) => field.onChange(e.target.value === "" ? null : e.target.value)}
                              />
                            </FormControl>
                            <FormDescription className="text-xs">0.1 – 20.0</FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="maxDebtToEbitda"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Max Debt-to-EBITDA</FormLabel>
                            <FormControl>
                              <Input
                                type="number"
                                step="0.1"
                                placeholder="e.g. 4.0"
                                data-testid="input-max-debt-to-ebitda"
                                value={field.value ?? ""}
                                onChange={(e) => field.onChange(e.target.value === "" ? null : e.target.value)}
                              />
                            </FormControl>
                            <FormDescription className="text-xs">0.1 – 20.0</FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    {/* Array fields */}
                    <div className="grid grid-cols-1 gap-4">
                      <FormField
                        control={form.control}
                        name="acceptableSecurityTypes"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Acceptable Security Types</FormLabel>
                            <FormControl>
                              <Input
                                placeholder="e.g. residential_property, commercial_property"
                                data-testid="input-acceptable-security-types"
                                value={arrayToString(field.value as string[] | null | undefined)}
                                onChange={(e) => field.onChange(stringToArray(e.target.value))}
                              />
                            </FormControl>
                            <FormDescription className="text-xs">Comma-separated list. Leave blank for no restriction.</FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="acceptableEntityTypes"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Acceptable Entity Types</FormLabel>
                            <FormControl>
                              <Input
                                placeholder="e.g. company, trust"
                                data-testid="input-acceptable-entity-types"
                                value={arrayToString(field.value as string[] | null | undefined)}
                                onChange={(e) => field.onChange(stringToArray(e.target.value))}
                              />
                            </FormControl>
                            <FormDescription className="text-xs">Comma-separated list. Leave blank for no restriction.</FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="geographicRestrictions"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Geographic Restrictions</FormLabel>
                            <FormControl>
                              <Input
                                placeholder="e.g. NSW, VIC, QLD"
                                data-testid="input-geographic-restrictions"
                                value={arrayToString(field.value as string[] | null | undefined)}
                                onChange={(e) => field.onChange(stringToArray(e.target.value))}
                              />
                            </FormControl>
                            <FormDescription className="text-xs">AU state codes. Leave blank for no restriction.</FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    {/* Checkbox flags */}
                    <div className="flex gap-6">
                      <FormField
                        control={form.control}
                        name="requiresFullDoc"
                        render={({ field }) => (
                          <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                            <FormControl>
                              <Checkbox
                                checked={field.value ?? true}
                                onCheckedChange={field.onChange}
                                data-testid="checkbox-requires-full-doc"
                              />
                            </FormControl>
                            <div className="space-y-1 leading-none">
                              <FormLabel>Requires Full Doc</FormLabel>
                              <FormDescription className="text-xs">
                                Product requires full documentation
                              </FormDescription>
                            </div>
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="requiresExistingCustomer"
                        render={({ field }) => (
                          <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                            <FormControl>
                              <Checkbox
                                checked={field.value ?? false}
                                onCheckedChange={field.onChange}
                                data-testid="checkbox-requires-existing-customer"
                              />
                            </FormControl>
                            <div className="space-y-1 leading-none">
                              <FormLabel>Requires Existing Customer</FormLabel>
                              <FormDescription className="text-xs">
                                Only available to existing customers
                              </FormDescription>
                            </div>
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>
                </CollapsibleContent>
              </div>
            </Collapsible>

            {/* Audit Information - Read Only (only visible in edit mode) */}
            {isEdit && product && (
              <div className="space-y-4 border-t pt-4">
                <h3 className="text-sm font-semibold text-muted-foreground">Audit Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Date Created</label>
                    <div className="mt-1 text-sm bg-muted/50 px-3 py-2 rounded-md" data-testid="display-created-at">
                      {product.createdAt
                        ? new Date(product.createdAt).toLocaleString("en-AU", {
                            dateStyle: "medium",
                            timeStyle: "short",
                          })
                        : "-"}
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Date Updated</label>
                    <div className="mt-1 text-sm bg-muted/50 px-3 py-2 rounded-md" data-testid="display-updated-at">
                      {product.updatedAt
                        ? new Date(product.updatedAt).toLocaleString("en-AU", {
                            dateStyle: "medium",
                            timeStyle: "short",
                          })
                        : "-"}
                    </div>
                  </div>
                </div>
              </div>
            )}

            <DialogFooter className="gap-2 mt-6">
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleOpenChange(false)}
                  disabled={isPending}
                  data-testid="button-cancel"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isPending}
                  data-testid="button-save"
                >
                  {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {isEdit ? "Update" : "Create"} Product
                </Button>
              </div>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
